import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import classNames from '../../utils/classNames'
import { numberWithCommas } from '../../utils/number'
import type { Item } from '../common/scroll-selector'
import Icon from '../icon'
import { TextInput } from '../register'
import type { PromoType } from '../wallet-management/deposit-form'

export type Promo = Item & {
  content: string
  bonus_amount: number
  total_credit_amount: number
}

interface PromotionInputProps {
  selectedPromo: Promo | null
  onPromoSelect: (promo: Promo) => void
  customPromoCode: string
  onCustomPromoChange: (promo: string) => void
  promoList: Promo[]
  showPromoCode: boolean
  promoCodeError: string | null
  promoType: PromoType
  setPromoType: (type: PromoType) => void
}

const Amount = ({ children }: { children: ReactNode }) => (
  <p className="lg:text-lg text-secondary-txt [&_span]:text-txt">{children}</p>
)

const PromotionInput = ({
  selectedPromo,
  promoList,
  onPromoSelect,
  customPromoCode,
  onCustomPromoChange,
  showPromoCode,
  promoCodeError,
  setPromoType,
}: PromotionInputProps) => {
  const { t } = useTranslation('walletManagement')
  const [isPromoCodeOpen, setIsPromoCodeOpen] = useState(false)
  const showAutoPromotion = promoList.length > 0
  const isPromoCodeExpanded = isPromoCodeOpen || !!promoCodeError
  return (
    <div className="mb-5 border-t border-tertiary pt-5">
      <div className="flex flex-col items-start gap-4">
        {showAutoPromotion && (
          <div className="flex gap-2 items-center flex-nowrap overflow-x-auto overflow-y-hidden line-clamp-1">
            {promoList.map(promo => (
              <button
                key={promo.id}
                className={classNames(
                  'w-[140px] lg:w-[180px] min-w-[140px] lg:min-w-[180px] p-2.5 text-left rounded-md border border-txt transition-all duration-200 ease-in-out disabled:opacity-70',
                  selectedPromo?.id && +selectedPromo.id === +promo.id
                    ? 'bg-highlight'
                    : 'bg-tertiary',
                )}
                onClick={() => {
                  if (showPromoCode) {
                    setPromoType('autopromo')
                    onCustomPromoChange('')
                  }
                  onPromoSelect(promo)
                }}>
                <p className="text-xs lg:text-sm text-txt leading-4 lg:leading-[18px] uppercase font-Geometr706-BlkCn-BT">
                  {promo.label}
                </p>
              </button>
            ))}
          </div>
        )}
        {showPromoCode && (
          <div className="w-full flex flex-col gap-4">
            <button
              type="button"
              className="w-full flex items-center justify-between gap-2 text-left"
              onClick={() => setIsPromoCodeOpen(prev => !prev)}>
              <span className="text-sm lg:text-base text-txt">
                {t('enter-promo-code')}
              </span>
              <Icon
                id="chevron-down"
                width="16"
                height="16"
                className={classNames(
                  'fill-txt transition-transform',
                  isPromoCodeExpanded ? 'rotate-180' : '',
                )}
              />
            </button>
            {isPromoCodeExpanded && (
              <>
                <div className="w-full flex items-center justify-between gap-2">
                  <TextInput
                    value={customPromoCode}
                    onChange={e => {
                      const value = e.currentTarget.value
                      onCustomPromoChange(value)
                      if (value) {
                        setPromoType('custom')
                        onPromoSelect({
                          label: '',
                          id: 0,
                          content: '',
                          bonus_amount: 0,
                          total_credit_amount: 0,
                        })
                      } else {
                        setPromoType(null)
                      }
                    }}
                    containerClassName="w-full !mb-0 !h-10 border border-txt"
                    className="!text-base"
                    placeholder={t('enter-promo-code')}
                  />
                </div>
                {promoCodeError && (
                  <div className="bg-red-500 px-3 text-white py-1 rounded-sm text-sm -mt-2 flex gap-2 items-center ml-0">
                    <Icon
                      id="warning"
                      width="16"
                      height="16"
                      className="fill-red-500"
                    />
                    {promoCodeError}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {selectedPromo && selectedPromo.id !== 0 && (
        <div
          className="flx-str-ctr mt-4"
          style={{
            flexWrap: 'wrap',
            gap: 12,
            justifyContent: 'space-between',
          }}>
          <Amount>
            {t('bonus-amount')}:{' '}
            <span>${numberWithCommas(+selectedPromo.bonus_amount || 0)}</span>
          </Amount>
          <Amount>
            {t('total-credits')}:{' '}
            <span>
              ${numberWithCommas(+selectedPromo.total_credit_amount || 0)}
            </span>
          </Amount>
        </div>
      )}
    </div>
  )
}

export default PromotionInput
